import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface GameState {
  gameStarted: boolean;
  gameEnded: boolean;
  score: number;
  baseScore: number;
  timeBonusScore: number;
  currentLevel: number;
  isGameCompleted: boolean;
  showLevelScore: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class GameStateManagerService {
  private gameStates = new Map<string, GameState>();
  private activeGame: string | null = null;
  
  // Observable to notify components of state changes
  private stateChangeSubject = new BehaviorSubject<{gameId: string, state: GameState} | null>(null);
  public stateChange$ = this.stateChangeSubject.asObservable();

  constructor() {}

  /**
   * Initialize a new game state
   */
  initializeGame(gameId: string, initialState?: Partial<GameState>): GameState {
    const defaultState: GameState = {
      gameStarted: false,
      gameEnded: false,
      score: 0,
      baseScore: 0,
      timeBonusScore: 0,
      currentLevel: 1,
      isGameCompleted: false,
      showLevelScore: false,
      ...initialState
    };

    this.gameStates.set(gameId, defaultState);
    this.activeGame = gameId;
    this.stateChangeSubject.next({gameId, state: defaultState});
    
    return defaultState;
  }

  /**
   * Update game state
   */
  updateGameState(gameId: string, updates: Partial<GameState>): GameState | null {
    const currentState = this.gameStates.get(gameId);
    if (!currentState) {
      console.warn(`Game state not found for gameId: ${gameId}`);
      return null;
    }

    const updatedState = { ...currentState, ...updates };
    this.gameStates.set(gameId, updatedState);
    this.stateChangeSubject.next({gameId, state: updatedState});
    
    return updatedState;
  }

  /**
   * Get current game state
   */
  getGameState(gameId: string): GameState | null {
    return this.gameStates.get(gameId) || null;
  }

  /**
   * Reset game state to initial values
   */
  resetGameState(gameId: string): GameState {
    const resetState: GameState = {
      gameStarted: false,
      gameEnded: false,
      score: 0,
      baseScore: 0,
      timeBonusScore: 0,
      currentLevel: 1,
      isGameCompleted: false,
      showLevelScore: false
    };

    this.gameStates.set(gameId, resetState);
    this.stateChangeSubject.next({gameId, state: resetState});
    
    return resetState;
  }

  /**
   * Completely clear game state and remove from memory
   */
  clearGameState(gameId: string): void {
    this.gameStates.delete(gameId);
    if (this.activeGame === gameId) {
      this.activeGame = null;
    }
  }

  /**
   * Clear all game states (useful for logout or app reset)
   */
  clearAllGameStates(): void {
    this.gameStates.clear();
    this.activeGame = null;
    this.stateChangeSubject.next(null);
  }

  /**
   * Set the currently active game
   */
  setActiveGame(gameId: string): void {
    this.activeGame = gameId;
  }

  /**
   * Get the currently active game
   */
  getActiveGame(): string | null {
    return this.activeGame;
  }

  /**
   * Check if a game is currently active
   */
  isGameActive(gameId: string): boolean {
    const state = this.getGameState(gameId);
    return state ? state.gameStarted && !state.gameEnded : false;
  }

  /**
   * Force cleanup of any active game states
   */
  forceCleanupActiveGames(): void {
    for (const [gameId, state] of this.gameStates.entries()) {
      if (state.gameStarted && !state.gameEnded) {
        this.updateGameState(gameId, {
          gameStarted: false,
          gameEnded: true,
          isGameCompleted: false
        });
      }
    }
  }
} 